/**
 * VistoeraTASK-00557 - first-level Image Asset classification.
 *
 * Exactly four cards, in this order: 全部图片 -> 上传图 -> AI生成 -> 编辑生成.
 * The classification is decided by the SERVER (`item.origin`, counts in
 * `LibraryListResponse.origins`); the browser never infers it from filenames,
 * content or the fine-grained `asset_type`.
 */
import type { ImageOrigin, ImageOriginCounts, LibraryAssetItem } from '../../api/client'

export const ORIGIN_CARD_KEYS = ['all', 'upload', 'ai_generated', 'edit_generated'] as const
export type OriginCardKey = (typeof ORIGIN_CARD_KEYS)[number]

export const EMPTY_ORIGIN_COUNTS: ImageOriginCounts = {
  all: 0,
  upload: 0,
  ai_generated: 0,
  edit_generated: 0,
}

/** First-level origin of a row (rows from an older server default to upload). */
export function originOf(item: Pick<LibraryAssetItem, 'origin'>): ImageOrigin {
  const o = item.origin
  return o === 'ai_generated' || o === 'edit_generated' ? o : 'upload'
}

export function originBadgeClass(item: Pick<LibraryAssetItem, 'origin'>): string {
  return `iac-badge iac-origin-${originOf(item)}`
}

export function normalizeOriginCounts(raw: Partial<ImageOriginCounts> | null | undefined, fallbackTotal = 0): ImageOriginCounts {
  if (!raw) return { ...EMPTY_ORIGIN_COUNTS, all: fallbackTotal }
  return {
    all: raw.all ?? fallbackTotal,
    upload: raw.upload ?? 0,
    ai_generated: raw.ai_generated ?? 0,
    edit_generated: raw.edit_generated ?? 0,
  }
}

/**
 * Optimistic delete math for the four cards (never mutates `prev`, never goes
 * negative). `delta=-1` removes, `delta=1` rolls back.
 */
export function applyOriginDeleteDelta(
  prev: ImageOriginCounts,
  removed: Pick<LibraryAssetItem, 'origin'>[],
  delta: 1 | -1,
): ImageOriginCounts {
  const next: ImageOriginCounts = { ...prev }
  for (const item of removed) {
    const key = originOf(item)
    next.all = Math.max(0, next.all + delta)
    next[key] = Math.max(0, next[key] + delta)
  }
  return next
}
